import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import ChatBot from '../components/ChatBot';
import NewsletterCapture from '../components/NewsletterCapture';
import { CITIES } from '../data/cities';
import AutopilotShowcase from '../components/autopilot/AutopilotShowcase';
import { 
  Calendar, 
  CheckCircle,
  ArrowRight,
  Star,
  Zap,
  Menu,
  X,
  RefreshCw,
  Users,
  BarChart3,
  FileText,
  MessageSquare,
  Shield,
  Clock,
  ChevronDown,
  TrendingUp,
  Heart,
  Home,
  Euro,
  Bell,
  Lock,
  Smartphone,
  Globe,
  Award,
  Play,
  Check,
  Sparkles,
  Receipt,
  ClipboardCheck,
  Camera,
  KeyRound
} from 'lucide-react';

// Images de propriétés
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=70&auto=format&fm=webp",
  villa1: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&q=70&auto=format&fm=webp",
  villa2: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800&q=70&auto=format&fm=webp",
  villa3: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=70&auto=format&fm=webp",
  // Photos de personnes pour témoignages
  person1: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&q=70&auto=format&fm=webp",
  person2: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&q=70&auto=format&fm=webp",
  person3: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=120&q=70&auto=format&fm=webp",
  person4: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=120&q=70&auto=format&fm=webp",
  person5: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=120&q=70&auto=format&fm=webp",
  person6: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=120&q=70&auto=format&fm=webp"
};

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [animatedStats, setAnimatedStats] = useState({ time: 0, revenue: 0, properties: 0 });

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedStats({ time: 15, revenue: 2847, properties: 12500 });
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Fonctionnalités V5
  const features = [
    {
      icon: RefreshCw,
      title: "Synchronisation automatique",
      description: "Calendriers Airbnb, Booking et Abritel synchronisés toutes les 10 minutes. Zéro double réservation garantie.",
      color: "bg-blue-50 text-blue-600"
    },
    {
      icon: MessageSquare,
      title: "Messages automatiques",
      description: "Confirmations, bienvenue, rappels check-in envoyés automatiquement. Vos voyageurs sont informés sans effort.",
      color: "bg-rose-50 text-rose-600"
    },
    {
      icon: Lock,
      title: "Check-in autonome",
      description: "Intégration serrures connectées. Vos voyageurs reçoivent leur code d'accès automatiquement.",
      color: "bg-blue-50 text-blue-600"
    },
    {
      icon: Euro,
      title: "Pilotage financier temps réel",
      description: "Revenus nets, charges, taux d'occupation et projections 3 mois. Votre cockpit financier toujours à jour.",
      color: "bg-emerald-50 text-emerald-600",
      badge: "NEW"
    },
    {
      icon: FileText,
      title: "Contrats légaux automatiques",
      description: "Générez vos contrats de location, conciergerie et sous-location en 2 clics. 100% conformes aux lois françaises 2026.",
      color: "bg-purple-50 text-purple-600",
      badge: "NOUVEAU"
    },
    {
      icon: ClipboardCheck,
      title: "État des lieux complet",
      description: "Inspection pièce par pièce avec photos, compteurs, clés. Export PDF pro et envoi email au locataire en 1 clic.",
      color: "bg-cyan-50 text-cyan-600",
      badge: "NOUVEAU"
    },
    {
      icon: Receipt,
      title: "Quittances de loyer",
      description: "Créez et envoyez des quittances de loyer PDF directement par email au locataire, conformes à la loi du 6 juillet 1989.",
      color: "bg-amber-50 text-amber-600",
      badge: "NOUVEAU"
    },
    {
      icon: BarChart3,
      title: "Tableaux de bord",
      description: "Taux d'occupation, revenus, performance par logement. Toutes vos données en un coup d'œil.",
      color: "bg-rose-50 text-rose-600"
    }
  ];

  // Témoignages avec photos
  const testimonials = [
    {
      name: "Sophie Martin",
      role: "8 appartements à Nice",
      content: "Avant OZCBNB, je passais mes soirées à jongler entre les plateformes. Aujourd'hui, tout est automatisé. J'ai retrouvé ma vie de famille et augmenté mon CA de 23%.",
      rating: 5,
      image: IMAGES.person1,
      highlight: "+23% de revenus"
    },
    {
      name: "Pierre Dubois",
      role: "12 logements à Paris",
      content: "J'ai testé Lodgify, Smoobu, SuperHote. OZCBNB est le seul qui combine simplicité et prix abordable. Et surtout : un seul prix pour tous mes logements !",
      rating: 5,
      image: IMAGES.person2,
      highlight: "12 logements, 1 prix"
    },
    {
      name: "Marie-Claire Laurent",
      role: "Conciergerie Côte d'Azur",
      content: "Mes 6 femmes de ménage utilisent l'application tous les jours. Plus aucune confusion sur les missions. Le gain de temps est énorme.",
      rating: 5,
      image: IMAGES.person3,
      highlight: "-70% de temps admin"
    },
    {
      name: "Thomas Bertrand",
      role: "5 gîtes en Dordogne",
      content: "Le support est exceptionnel. Une vraie personne qui répond en français, pas un robot. En 2 ans, jamais une seule double réservation.",
      rating: 5,
      image: IMAGES.person4,
      highlight: "0 double réservation"
    },
    {
      name: "Émilie Rousseau",
      role: "Studio à Lyon",
      content: "Je débutais sur Airbnb, j'étais complètement perdue. OZCBNB m'a tout simplifié. Interface claire, prix honnête. Je recommande à 100%.",
      rating: 5,
      image: IMAGES.person5,
      highlight: "Idéal débutants"
    },
    {
      name: "Jean-Marc Petit",
      role: "15 biens en Bretagne",
      content: "Avec 15 logements sur 3 plateformes différentes, c'était l'enfer. Maintenant tout est centralisé. J'ai gagné 20 heures par semaine.",
      rating: 5,
      image: IMAGES.person6,
      highlight: "+20h/semaine"
    }
  ];

  // FAQ
  const faqs = [
    {
      question: "Combien coûte OZCBNB ?",
      answer: "29,90€ HT/mois, c'est tout. Ce prix unique inclut TOUS vos logements, que vous en ayez 1 ou 100. Pas de frais cachés, pas de commission sur vos réservations, pas de surcoût par propriété. Un seul abonnement, toutes les fonctionnalités."
    },
    {
      question: "En quoi OZCBNB est différent des autres logiciels ?",
      answer: "Contrairement à Lodgify (qui prend 1,2% par réservation), Smoobu (qui facture par logement) ou SuperHote (39€/mois), OZCBNB propose un prix unique pour un nombre illimité de logements. Vous payez 29,90€ HT que vous ayez 2 ou 50 propriétés."
    },
    {
      question: "Comment fonctionne la synchronisation des calendriers ?",
      answer: "OZCBNB se connecte à vos comptes Airbnb, Booking et Abritel via iCal. Vos calendriers sont synchronisés automatiquement toutes les 10 minutes. Quand une réservation arrive, les dates sont instantanément bloquées sur toutes les plateformes. Zéro double réservation."
    },
    {
      question: "Mes équipes de ménage doivent-elles payer ?",
      answer: "Non, jamais. L'accès est 100% gratuit et illimité pour vos prestataires. Invitez autant de personnes que vous voulez : femmes de ménage, agents d'entretien, gestionnaires. Ils reçoivent leurs missions et peuvent les valider depuis l'application."
    },
    {
      question: "Y a-t-il un engagement minimum ?",
      answer: "Aucun engagement. Vous pouvez annuler votre abonnement à tout moment depuis votre espace client. L'annulation prend effet en fin de mois. Pas de frais de résiliation, pas de questions."
    },
    {
      question: "Le support est-il vraiment en français ?",
      answer: "Oui, notre équipe est basée en France. Vous échangez avec de vraies personnes, pas des robots. Réponse garantie sous 24h, souvent en quelques heures. Nous sommes là pour vous accompagner."
    },
    {
      question: "Comment fonctionne l'état des lieux numérique ?",
      answer: "Créez un état des lieux d'entrée ou de sortie en 10 minutes : ajoutez vos pièces, notez l'état de chaque élément (murs, sol, plafond, équipements), prenez des photos directement depuis votre téléphone, relevez les compteurs et les clés. Exportez un PDF professionnel conforme au décret du 30 mars 2016 et envoyez-le directement par email au locataire depuis OZCBNB."
    },
    {
      question: "Les contrats générés sont-ils conformes au droit français ?",
      answer: "Oui. Nos modèles de contrats (bail civil, sous-location professionnelle, contrat de conciergerie) sont rédigés en conformité avec le Code civil, la loi ALUR du 24 mars 2014, la loi ELAN et le cadre légal français en vigueur. Ils incluent toutes les clauses obligatoires : état des lieux, dépôt de garantie, durée, conditions de résiliation, obligations des parties, etc."
    },
    {
      question: "Qu'est-ce que le check-in autonome ?",
      answer: "Le check-in autonome permet à vos voyageurs de recevoir un lien digital avec toutes les instructions d'arrivée : code d'accès, emplacement de la boîte à clés, WiFi, règles de la maison, numéros d'urgence. Plus besoin d'être présent à l'arrivée de chaque voyageur."
    },
    {
      question: "Puis-je gérer plusieurs plateformes (Airbnb, Booking, Abritel) ?",
      answer: "Absolument. OZCBNB synchronise automatiquement vos calendriers sur Airbnb, Booking.com et Abritel via les liens iCal. Les réservations sont importées en temps réel et les disponibilités mises à jour sur toutes les plateformes simultanément."
    },
    {
      question: "Comment fonctionne la gestion d'équipe ?",
      answer: "Vous invitez vos prestataires par email. Ils reçoivent un accès gratuit à l'application avec leurs missions, les propriétés assignées et les calendriers de réservation. Vous pouvez définir des rôles (ménage, maintenance, responsable), suivre leurs performances et gérer les assignations de logements."
    },
    {
      question: "Mes données sont-elles sécurisées et sauvegardées ?",
      answer: "Oui. Vos données sont stockées de manière sécurisée avec des sauvegardes automatiques quotidiennes. Vous pouvez également exporter vos données à tout moment depuis votre espace (conformité RGPD). Toutes les communications sont chiffrées en HTTPS."
    },
    {
      question: "Comment maximiser mon taux d'occupation ?",
      answer: "OZCBNB vous donne tous les outils : automatisation des messages, tableaux de bord temps réel avec taux d'occupation, Selena, l'IA voyageurs 24/7 qui répond à votre place et booste vos avis 5 étoiles. L'assistant IA peut aussi analyser vos performances et vous suggérer des améliorations."
    },
    {
      question: "Peut-on exporter les données comptables ?",
      answer: "Oui. OZCBNB offre un export CSV complet de vos données financières : revenus, dépenses, commissions par plateforme, frais de ménage. Idéal pour votre comptable ou votre déclaration de revenus. Les rapports sont filtrables par mois, par logement et par catégorie."
    },
    {
      question: "Je suis débutant sur Airbnb, OZCBNB est-il adapté ?",
      answer: "Parfaitement ! OZCBNB est conçu pour être simple d'utilisation, que vous ayez 1 ou 50 logements. L'interface est intuitive, l'assistant IA vous guide, et le support en français est là pour répondre à toutes vos questions. Nous accompagnons de nombreux débutants dans leur lancement."
    }
  ];

  return (
    <>
      <Helmet>
        <title>OZCBNB | Logiciel Gestion Airbnb & Booking - Un Prix, Logements Illimités</title>
        <meta name="description" content="OZCBNB : le logiciel de gestion Airbnb et Booking n°1 en France. 29,90€ HT/mois pour tous vos logements. Synchronisation automatique, messages auto, gestion équipes. +500 propriétaires nous font confiance." />
      </Helmet>

      <div className="min-h-screen overflow-x-hidden bg-[#0A0A0F] font-sans antialiased text-white relative [&_h1]:!text-white [&_h2]:!text-white [&_h3]:!text-white [&_h4]:!text-white [&_p]:!text-inherit">
        {/* Ambient lights */}
        <div className="pointer-events-none fixed inset-0 z-0">
          <div className="absolute -top-40 left-[20%] w-[700px] h-[700px] bg-violet-600/[0.08] blur-[140px] rounded-full" />
          <div className="absolute top-[40%] -right-40 w-[600px] h-[600px] bg-blue-600/[0.06] blur-[140px] rounded-full" />
          <div className="absolute bottom-0 -left-40 w-[600px] h-[600px] bg-emerald-600/[0.05] blur-[140px] rounded-full" />
        </div>

        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0F]/80 backdrop-blur-xl border-b border-white/[0.06]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="text-xl font-bold text-white tracking-tight">
                OZCBNB
              </Link>
              
              <div className="hidden md:flex items-center gap-6 lg:gap-8">
                <Link to="/fonctionnalites" className="text-sm text-white/60 hover:text-white transition-colors">Fonctionnalités</Link>
                <Link to="/tarifs" className="text-sm text-white/60 hover:text-white transition-colors">Tarifs</Link>
                <Link to="/comparatif" className="text-sm text-white/60 hover:text-white transition-colors">Comparatif</Link>
                <Link to="/blog" className="text-sm text-white/60 hover:text-white transition-colors">Blog</Link>
              </div>

              <div className="hidden md:flex items-center gap-3">
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/[0.06]">Connexion</Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="bg-white hover:bg-white/90 text-black rounded-full px-5 font-semibold">
                    Commencer
                  </Button>
                </Link>
              </div>

              <button className="md:hidden p-2 text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Menu Mobile */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-[#0A0A0F]/95 backdrop-blur-xl border-t border-white/[0.06] py-4 px-4">
              <div className="flex flex-col gap-3">
                <Link to="/fonctionnalites" className="py-2 text-white/80 font-medium">Fonctionnalités</Link>
                <Link to="/tarifs" className="py-2 text-white/80 font-medium">Tarifs</Link>
                <Link to="/comparatif" className="py-2 text-white/80 font-medium">Comparatif</Link>
                <Link to="/blog" className="py-2 text-white/80 font-medium">Blog</Link>
                <hr className="my-2 border-white/[0.06]" />
                <Link to="/login" className="py-2 text-white/60">Connexion</Link>
                <Link to="/register">
                  <Button className="w-full bg-white text-black rounded-full">
                    Commencer maintenant
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </nav>

        {/* Hero Section - PREMIUM DARK */}
        <section className="relative pt-28 pb-16 md:pt-36 md:pb-24 px-4 sm:px-6 z-10">
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-4xl mx-auto mb-14">
              {/* Live social proof badge */}
              <div className="inline-flex items-center gap-2 bg-white/[0.04] backdrop-blur-xl border border-white/[0.08] rounded-full px-4 py-1.5 text-xs text-white/70 mb-3 animate-in fade-in slide-in-from-bottom-2 duration-500">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                </span>
                <span className="font-medium">12 500+ logements déjà gérés en France</span>
              </div>
              {/* Fondateur français + moins cher du marché */}
              <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="hero-badges-fr">
                <span className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500/10 via-white/[0.06] to-rose-500/10 border border-white/[0.08] rounded-full px-3 py-1.5 text-xs font-semibold text-white/80">
                  <span aria-hidden>🇫🇷</span> Logiciel 100% français, fondé par un Français
                </span>
                <span className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1.5 text-xs font-semibold text-emerald-300">
                  Le moins cher du marché
                </span>
                <span className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-3 py-1.5 text-xs font-semibold text-amber-300">
                  Prêt en 30 minutes
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-6 animate-in fade-in slide-in-from-bottom-3 duration-700">
                <span className="bg-gradient-to-br from-white via-white to-white/40 bg-clip-text text-transparent">
                  Logiciel de gestion
                </span>
                <br />
                <span className="bg-gradient-to-r from-violet-400 via-blue-400 to-emerald-400 bg-clip-text text-transparent">
                  Airbnb &amp; Booking a prix fixe
                </span>
              </h1>

              <p className="text-base sm:text-lg md:text-xl text-white/60 mb-8 leading-relaxed max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-1000">
                Centralisez vos reservations, synchronisez Airbnb, Booking et Abritel, automatisez vos messages et gerez tous vos logements avec un seul abonnement :{' '}
                <strong className="text-white">29,90 € HT/mois <span className="text-white/50 font-normal">(35,88 € TTC, TVA 20%)</span>, sans commission.</strong> Tout est <strong className="text-white">prêt en 30 minutes</strong>, sans formation.
              </p>

              {/* 3 bénéfices visibles au-dessus de la ligne de flottaison */}
              <div className="flex flex-wrap justify-center gap-3 mb-8 animate-in fade-in slide-in-from-bottom-4 duration-1000" data-testid="hero-benefits">
                {[
                  '29,90 € HT/mois pour tous vos logements',
                  'Logements illimités',
                  '0 € de commission',
                ].map((b, i) => (
                  <span key={i} className="inline-flex items-center gap-2 bg-white/[0.05] border border-white/[0.10] backdrop-blur-sm rounded-full px-4 py-2 text-sm font-semibold text-white/90">
                    <Check className="w-4 h-4 text-emerald-400" />{b}
                  </span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8 animate-in fade-in slide-in-from-bottom-5 duration-1000">
                <Link to="/register" className="w-full sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto bg-white hover:bg-white/90 text-black rounded-full px-8 h-14 text-base font-semibold shadow-[0_20px_60px_-15px_rgba(255,255,255,0.4)]" data-testid="hero-cta-register">
                    Commencer maintenant
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/fonctionnalites" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto rounded-full px-6 h-14 text-base bg-white/[0.04] border-white/[0.12] text-white hover:bg-white/[0.08] hover:text-white">
                    Voir les fonctionnalités
                  </Button>
                </Link>
              </div>

              <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-white/40">
                <div className="flex items-center gap-1.5">
                  <Check className="w-4 h-4 text-emerald-400" />
                  <span>Sans engagement</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Check className="w-4 h-4 text-emerald-400" />
                  <span>Pas de carte requise</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Check className="w-4 h-4 text-emerald-400" />
                  <span>Support français</span>
                </div>
              </div>
            </div>

            {/* Floating preview card with glassmorphism */}
            <div className="relative mx-auto max-w-5xl">
              <div className="relative bg-white/[0.03] backdrop-blur-2xl border border-white/[0.08] rounded-3xl overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.6)]">
                <div className="absolute inset-0 bg-gradient-to-br from-violet-600/10 via-transparent to-blue-600/10 pointer-events-none" />
                <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-4 p-6 sm:p-8">
                  {[
                    { label: 'Synchros / jour', value: '2 880', sub: 'toutes les 5 min', color: 'text-violet-300' },
                    { label: 'Réservations 2026', value: '12,5K+', sub: 'gérées', color: 'text-blue-300' },
                    { label: 'Selena IA', value: '24/7', sub: 'réponses < 2s', color: 'text-emerald-300' },
                    { label: 'Avis clients', value: '4.9/5', sub: 'sur 500+', color: 'text-amber-300' },
                  ].map((s, i) => (
                    <div key={i} className="bg-white/[0.03] border border-white/[0.05] rounded-2xl p-4 hover:bg-white/[0.06] transition-colors">
                      <p className="text-[10px] uppercase tracking-[0.16em] text-white/40 font-medium">{s.label}</p>
                      <p className={`text-2xl sm:text-3xl font-bold ${s.color} mt-1.5 tracking-tight`}>{s.value}</p>
                      <p className="text-[11px] text-white/40 mt-0.5">{s.sub}</p>
                    </div>
                  ))}
                </div>

                {/* Live booking notification */}
                <div className="border-t border-white/[0.06] px-5 py-3 flex items-center gap-3 text-xs text-white/60">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                  </span>
                  <span>Sophie M. vient de réserver <strong className="text-white">Villa Cap d'Antibes</strong> · il y à 2 min</span>
                </div>
              </div>
              {/* glow accent under card */}
              <div className="absolute -inset-x-20 -bottom-10 h-40 bg-gradient-to-r from-violet-500/0 via-violet-500/20 to-blue-500/0 blur-3xl pointer-events-none" />
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="relative py-12 md:py-16 px-4 sm:px-6 border-y border-white/[0.06] z-10">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 text-center">
              <div>
                <p className="text-3xl sm:text-4xl font-bold text-white">{animatedStats.properties.toLocaleString()}+</p>
                <p className="text-sm text-white/40 mt-1">Logements gérés</p>
              </div>
              <div>
                <p className="text-3xl sm:text-4xl font-bold text-white">500+</p>
                <p className="text-sm text-white/40 mt-1">Propriétaires actifs</p>
              </div>
              <div>
                <p className="text-3xl sm:text-4xl font-bold text-white">{animatedStats.time}h</p>
                <p className="text-sm text-white/40 mt-1">Gagnées / semaine</p>
              </div>
              <div>
                <p className="text-3xl sm:text-4xl font-bold text-white">4.9/5</p>
                <p className="text-sm text-white/40 mt-1">Satisfaction</p>
              </div>
            </div>
          </div>
        </section>

        {/* Partenaires */}
        <section className="relative py-10 md:py-12 px-4 sm:px-6 z-10">
          <div className="max-w-6xl mx-auto">
            <p className="text-center text-sm text-white/40 mb-6 md:mb-8">Compatible avec toutes vos plateformes</p>
            <div className="flex flex-wrap justify-center items-center gap-6 sm:gap-8 md:gap-12">
              {[{ icon: 'svg', label: 'Airbnb' }, { icon: Globe, label: 'Booking.com' }, { icon: Home, label: 'Abritel' }, { icon: Calendar, label: 'iCal' }].map((p, i) => {
                const Ic = p.icon === 'svg' ? null : p.icon;
                return (
                  <div key={i} className="flex items-center gap-2 text-white/30">
                    {Ic ? <Ic className="w-6 h-6 sm:w-7 sm:h-7" /> : <span className="w-7 h-7 rounded-full bg-rose-500/20" />}
                    <span className="font-medium text-sm sm:text-base">{p.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <AutopilotShowcase />

        {/* Avantage principal : Prix unique */}
        <section className="py-16 md:py-24 px-4 sm:px-6 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 md:mb-6">
              Un seul prix. Tous vos logements.
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-blue-100 mb-8 md:mb-10 max-w-2xl mx-auto">
              Contrairement aux autres logiciels qui facturent par logement ou prennent une commission, 
              OZCBNB propose un <strong className="text-white">abonnement unique à 29,90€ HT/mois</strong> pour 
              gérer autant de propriétés que vous voulez.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 sm:gap-6 max-w-3xl mx-auto">
              <div className="bg-white/10 backdrop-blur rounded-2xl p-4 sm:p-6">
                <p className="text-3xl sm:text-4xl font-bold mb-2">1</p>
                <p className="text-blue-100 text-sm sm:text-base">Abonnement unique</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-2xl p-4 sm:p-6">
                <p className="text-3xl sm:text-4xl font-bold mb-2">∞</p>
                <p className="text-blue-100 text-sm sm:text-base">Logements illimités</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-2xl p-4 sm:p-6">
                <p className="text-3xl sm:text-4xl font-bold mb-2">0€</p>
                <p className="text-blue-100 text-sm sm:text-base">Commission</p>
              </div>
            </div>
          </div>
        </section>

        {/* Fonctionnalités */}
        <section className="py-16 md:py-24 px-4 sm:px-6 bg-transparent">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <span className="inline-block bg-rose-100 text-rose-700 text-sm font-medium px-4 py-1 rounded-full mb-4">
                Fonctionnalités complètes
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
                Tout ce qu'il vous faut pour réussir
              </h2>
              <p className="text-base sm:text-lg text-white/60 max-w-2xl mx-auto">
                Les mêmes fonctionnalités que les logiciels à 50€+/mois, à un prix juste.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="bg-white/[0.03] backdrop-blur-xl rounded-2xl p-5 sm:p-6 border border-white/[0.06] hover:border-white/20 hover:bg-white/[0.06] transition-all duration-300"
                >
                  <div className={`w-12 h-12 ${feature.color} rounded-xl flex items-center justify-center mb-4`}>
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-10 md:mt-12">
              <Link to="/fonctionnalites">
                <Button variant="outline" className="rounded-full px-6">
                  Voir toutes les fonctionnalités
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Galerie */}
        <section className="py-16 md:py-24 px-4 sm:px-6 bg-transparent">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
                Des propriétés d'exception
              </h2>
              <p className="text-base sm:text-lg text-white/60">
                Villas, appartements, gîtes... Tous types de biens, une seule solution.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
              <div className="aspect-[4/3] rounded-xl sm:rounded-2xl overflow-hidden">
                <img src={IMAGES.villa1} alt="Villa moderne" width="800" height="600" loading="lazy" decoding="async" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="aspect-[4/3] rounded-xl sm:rounded-2xl overflow-hidden">
                <img src={IMAGES.villa2} alt="Maison contemporaine" width="800" height="600" loading="lazy" decoding="async" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="aspect-[4/3] rounded-xl sm:rounded-2xl overflow-hidden sm:col-span-2 md:col-span-1">
                <img src={IMAGES.villa3} alt="Propriété de luxe" width="800" height="600" loading="lazy" decoding="async" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </div>
            </div>
          </div>
        </section>

        {/* Témoignages */}
        <section className="py-16 md:py-24 px-4 sm:px-6 bg-transparent">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-16">
              <span className="inline-block bg-blue-100 text-blue-700 text-sm font-medium px-4 py-1 rounded-full mb-4">
                Témoignages clients
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
                Ils ont fait confiance à OZCBNB
              </h2>
              <p className="text-base sm:text-lg text-white/60">
                Plus de 500 propriétaires et conciergeries en France
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {testimonials.map((t, index) => (
                <div key={index} className="bg-white/[0.03] backdrop-blur-xl rounded-2xl p-5 sm:p-6 border border-white/[0.06] hover:bg-white/[0.06] hover:border-white/15 transition-all">
                  {/* Highlight */}
                  <div className="inline-block bg-gradient-to-r from-blue-100 to-rose-100 text-white text-xs font-medium px-3 py-1 rounded-full mb-4">
                    {t.highlight}
                  </div>
                  
                  {/* Stars */}
                  <div className="flex gap-1 mb-3">
                    {[...Array(t.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  
                  {/* Content */}
                  <p className="text-white/75 mb-5 text-sm leading-relaxed">"{t.content}"</p>
                  
                  {/* Author */}
                  <div className="flex items-center gap-3">
                    <img 
                      src={t.image} 
                      alt={t.name}
                      width="48"
                      height="48"
                      loading="lazy"
                      decoding="async"
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-white shadow"
                    />
                    <div>
                      <p className="font-medium text-white text-sm">{t.name}</p>
                      <p className="text-xs text-white/45">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* État des Lieux Section - NEW */}
        <section className="py-16 md:py-24 px-4 sm:px-6 bg-transparent relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-300 rounded-full px-4 py-2 text-sm font-bold mb-6 border border-cyan-500/30">
                  <ClipboardCheck className="w-4 h-4" />
                  NOUVEAU — ÉTAT DES LIEUX PRO
                </div>

                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
                  L'état des lieux <br/>
                  <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                    en 10 minutes chrono
                  </span>
                </h2>

                <p className="text-lg text-white/60 mb-8 leading-relaxed">
                  L'outil d'inspection le plus complet du marché. Pièce par pièce, avec photos,
                  compteurs et relevé des clés. <strong className="text-white">Export PDF pro</strong> et
                  envoi direct par email au locataire.
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-cyan-500/10 border border-cyan-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Home className="w-5 h-5 text-cyan-300" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">Pièces dynamiques illimitées</p>
                      <p className="text-white/60 text-sm">Salon, chambres, cuisine, SDB, WC, extérieur — ajoutez ce que vous voulez</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-cyan-500/10 border border-cyan-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Camera className="w-5 h-5 text-cyan-300" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">Photos par pièce</p>
                      <p className="text-white/60 text-sm">Murs, sol, plafond, équipements — tout immortalisé pour zéro litige</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-cyan-500/10 border border-cyan-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Zap className="w-5 h-5 text-cyan-300" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">Compteurs & clés remises</p>
                      <p className="text-white/60 text-sm">Électricité, gaz, eau, nombre de clés — tout dans un seul document</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-cyan-500/10 border border-cyan-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-cyan-300" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">PDF signé + envoi email</p>
                      <p className="text-white/60 text-sm">Conforme au décret du 30 mars 2016, envoyé directement au locataire</p>
                    </div>
                  </div>
                </div>

                <Link to="/register">
                  <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white rounded-full px-8 h-14 text-base font-semibold shadow-xl shadow-cyan-500/25">
                    Créer un état des lieux
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>

              {/* Visual: fake EDL card */}
              <div className="bg-white/[0.03] backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/10">
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-white/50">Référence</p>
                    <p className="font-bold text-white">EDL-E-202605-A7F2</p>
                  </div>
                  <span className="text-xs font-semibold text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-3 py-1">
                    ENTRÉE
                  </span>
                </div>

                <div className="space-y-3">
                  {[
                    { room: "Salon", state: "Neuf", color: "bg-emerald-500", photos: 4 },
                    { room: "Cuisine", state: "Bon état", color: "bg-green-500", photos: 6 },
                    { room: "Chambre 1", state: "Bon état", color: "bg-green-500", photos: 3 },
                    { room: "Salle de bain", state: "État moyen", color: "bg-amber-500", photos: 5 },
                    { room: "WC", state: "Neuf", color: "bg-emerald-500", photos: 2 },
                  ].map((row, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-white/[0.03] rounded-xl border border-white/5">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${row.color}`}></div>
                        <p className="text-white font-medium text-sm">{row.room}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1 text-white/50 text-xs">
                          <Camera className="w-3 h-3" />
                          {row.photos}
                        </div>
                        <span className="text-xs text-white/70">{row.state}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-5 pt-5 border-t border-white/10 grid grid-cols-3 gap-3 text-center">
                  <div>
                    <KeyRound className="w-4 h-4 mx-auto text-cyan-300 mb-1" />
                    <p className="text-xs text-white/50">Clés</p>
                    <p className="text-white font-bold text-sm">3</p>
                  </div>
                  <div>
                    <Zap className="w-4 h-4 mx-auto text-cyan-300 mb-1" />
                    <p className="text-xs text-white/50">Compteurs</p>
                    <p className="text-white font-bold text-sm">4/4</p>
                  </div>
                  <div>
                    <Camera className="w-4 h-4 mx-auto text-cyan-300 mb-1" />
                    <p className="text-xs text-white/50">Photos</p>
                    <p className="text-white font-bold text-sm">20</p>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-xl text-center border border-cyan-500/20">
                  <p className="text-cyan-200 font-medium text-sm">
                    PDF prêt en 1 clic + envoi email automatique
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-16 md:py-24 px-4 sm:px-6 bg-transparent relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
                Un prix transparent, sans surprise
              </h2>
              <p className="text-base sm:text-lg text-white/60">
                Toutes les fonctionnalités. Tous vos logements. Un seul tarif.
              </p>
            </div>

            <div className="bg-white/[0.03] backdrop-blur-xl rounded-3xl border border-white/20 p-6 sm:p-8 md:p-12 relative overflow-hidden shadow-xl">
              <div className="absolute top-0 right-0 bg-gradient-to-r from-blue-600 to-rose-500 text-white px-4 sm:px-6 py-2 text-sm font-medium rounded-bl-2xl">
                Offre exclusive -50%
              </div>

              <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center pt-4 md:pt-0">
                <div>
                  <p className="text-white/45 mb-1">Abonnement unique &middot; Logiciel 100% français</p>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-4xl sm:text-5xl md:text-6xl font-bold text-white">29,90€</span>
                    <span className="text-white/45 text-base sm:text-lg">HT/mois</span>
                  </div>
                  <p className="text-sm text-white/45 mb-2">
                    <span className="line-through">59,90€ HT</span> &bull; Pour tous vos logements
                  </p>
                  <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-3 py-1.5 text-xs font-semibold text-amber-300 mb-6 sm:mb-8" data-testid="ht-mention-hero">
                    <span>Soit <strong>35,88€ TTC/mois</strong> (TVA 20% en sus)</span>
                  </div>

                  <Link to="/register">
                    <Button size="lg" className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-full h-14 text-base font-medium shadow-lg">
                      Commencer maintenant
                    </Button>
                  </Link>

                  <p className="text-center text-sm text-white/45 mt-4">
                    Prêt en 30 minutes &bull; Sans engagement &bull; Annulation libre
                  </p>
                </div>

                <div>
                  <p className="font-semibold text-white mb-4">Inclus dans votre abonnement :</p>
                  <ul className="space-y-3">
                    {[
                      "Logements illimités (2, 20 ou 200)",
                      "Synchronisation Airbnb, Booking, Abritel",
                      "Messages automatiques",
                      "Gestion équipes (gratuit pour eux)",
                      "Comptabilité et rapports",
                      "Support français prioritaire",
                      "Toutes les mises à jour"
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-white/75">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Comparaison */}
            <div className="mt-10 md:mt-12 text-center">
              <p className="text-sm text-white/45 mb-4">Comparez avec les autres solutions :</p>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-8 text-xs sm:text-sm text-white/60">
                <div>SuperHote : <span className="font-medium">39€/mois</span></div>
                <div>Lodgify : <span className="font-medium">32€ + 1,2%/résa</span></div>
                <div>Smoobu : <span className="font-medium">29€/logement</span></div>
              </div>
              <p className="text-sm text-white/60 mt-4 font-medium">
                Avec OZCBNB : <span className="text-blue-600">29,90€ HT pour TOUS vos logements</span>
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 px-4 sm:px-6 bg-transparent">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
                Questions fréquentes
              </h2>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white/[0.03] backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/[0.06] overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between p-4 sm:p-5 text-left"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span className="font-medium text-white pr-4 text-sm sm:text-base">{faq.question}</span>
                    <ChevronDown className={`w-5 h-5 text-white/45 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === index && (
                    <div className="px-4 sm:px-5 pb-4 sm:pb-5 text-white/60 text-sm leading-relaxed">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-16 md:py-24 px-4 sm:px-6 bg-gradient-to-r from-blue-600 via-blue-700 to-rose-600">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
              Prêt en 30 minutes. Pour de vrai.
            </h2>
            <p className="text-base sm:text-xl text-blue-100 mb-6 sm:mb-8">
              Logiciel <strong>100% français</strong>, fondé par un Français, le <strong>moins cher du marché</strong>. Inscription, ajout de vos logements et synchronisation Airbnb / Booking : tout est prêt en moins d'une demi-heure. Sans formation, sans appel commercial impose.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 rounded-full px-8 h-14 text-base font-medium shadow-xl" data-testid="cta-final-register">
                Commencer maintenant
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <p className="text-sm text-blue-200 mt-6">
              29,90 € HT/mois <span className="opacity-80">(35,88 € TTC, TVA 20%)</span> &middot; Logements illimités &middot; Sans engagement
            </p>
          </div>
        </section>

        {/* Newsletter capture */}
        <NewsletterCapture variant="section" source="landing-bottom" />

        {/* SEO — Grandes villes de France */}
        <section className="relative py-16 md:py-24 px-4 sm:px-6 border-t border-white/[0.06] z-10" data-testid="seo-cities-section">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl mb-10 md:mb-14">
              <span className="inline-block bg-white/[0.05] border border-white/[0.10] text-white/70 text-xs font-semibold px-4 py-1.5 rounded-full mb-5 uppercase tracking-wider">
                Disponible partout en France
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-5 leading-tight">
                Le logiciel de gestion Airbnb n°1 dans toutes les grandes villes de France
              </h2>
              <p className="text-base sm:text-lg text-white/60 leading-relaxed">
                Que vous louiez un studio à <strong className="text-white/90">Paris</strong>, un appartement à{' '}
                <strong className="text-white/90">Lyon</strong>, une villa à <strong className="text-white/90">Nice</strong>{' '}
                ou un gîte à <strong className="text-white/90">Bordeaux</strong>, OZCBNB centralise vos annonces Airbnb,
                Booking et Abritel, automatise le ménage et pilote vos finances. Un <strong className="text-white/90">channel
                manager sans commission</strong> pour la location courte durée, à <strong className="text-white/90">29,90 € HT/mois</strong>,
                logements illimités, dans chacune des grandes villes françaises.
              </p>
            </div>

            <div
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-3"
              data-testid="seo-cities-grid"
            >
              {CITIES.map((city) => (
                <Link
                  key={city.slug}
                  to={`/logiciel-gestion-airbnb/${city.slug}`}
                  data-testid={`seo-city-link-${city.slug}`}
                  className="text-sm text-white/55 hover:text-white transition-colors flex items-center gap-1.5 group"
                  title={`Logiciel de gestion Airbnb à ${city.name}`}
                >
                  <ArrowRight className="w-3 h-3 text-white/25 group-hover:text-emerald-400 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                  <span>Airbnb {city.name}</span>
                </Link>
              ))}
            </div>

            <p className="text-sm text-white/40 mt-10 max-w-3xl leading-relaxed">
              Propriétaires, conciergeries et sous-loueurs : OZCBNB vous accompagne dans la gestion de vos locations saisonnières
              à Paris, Marseille, Lyon, Toulouse, Nice, Nantes, Montpellier, Strasbourg, Bordeaux, Lille et dans toutes les
              communes touristiques de France. Un seul abonnement, tous vos logements, partout.{' '}
              <Link
                to="/logiciel-gestion-airbnb-france"
                className="text-white/70 hover:text-white underline font-medium"
                data-testid="seo-cities-hub-link"
              >
                Découvrir toutes les villes couvertes par OZCBNB
              </Link>.
            </p>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 sm:py-16 px-4 sm:px-6 bg-gray-900">
          <div className="max-w-7xl mx-auto">
            <div className="grid sm:grid-cols-2 md:grid-cols-5 gap-8 mb-12">
              <div className="sm:col-span-2 md:col-span-1">
                <p className="text-xl font-bold text-white mb-4">OZCBNB</p>
                <p className="text-sm text-white/30 leading-relaxed">
                  Le logiciel de gestion pour les propriétaires qui veulent retrouver leur sérénité.
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-white mb-4">Produit</p>
                <ul className="space-y-2 text-sm text-white/30">
                  <li><Link to="/fonctionnalites" className="hover:text-white transition-colors">Fonctionnalités</Link></li>
                  <li><Link to="/tarifs" className="hover:text-white transition-colors">Tarifs</Link></li>
                  <li><Link to="/comparatif" className="hover:text-white transition-colors">Comparatif</Link></li>
                  <li><Link to="/logiciel-gestion-airbnb-france" className="hover:text-white transition-colors" data-testid="footer-cities-link">Logiciel Airbnb par ville</Link></li>
                  {['paris', 'marseille', 'lyon', 'toulouse', 'nice', 'bordeaux', 'lille', 'nantes'].map((s) => (
                    <li key={s}><Link to={`/logiciel-gestion-airbnb/${s}`} className="hover:text-white transition-colors capitalize">Logiciel Airbnb {s}</Link></li>))}
                </ul>
              </div>
              <div>
                <p className="text-sm font-semibold text-white mb-4">Solutions</p>
                <ul className="space-y-2 text-sm text-white/30">
                  <li><Link to="/channel-manager-airbnb" className="hover:text-white transition-colors">Channel manager Airbnb</Link></li>
                  <li><Link to="/logiciel-gestion-airbnb" className="hover:text-white transition-colors">Logiciel gestion Airbnb</Link></li>
                  <li><Link to="/logiciel-conciergerie-airbnb" className="hover:text-white transition-colors">Logiciel conciergerie</Link></li>
                  <li><Link to="/synchronisation-airbnb-booking" className="hover:text-white transition-colors">Synchronisation Airbnb Booking</Link></li>
                  <li><Link to="/logiciel-airbnb-prix-fixe" className="hover:text-white transition-colors">Logiciel à prix fixe</Link></li>
                </ul>
              </div>
              <div>
                <p className="text-sm font-semibold text-white mb-4">Ressources</p>
                <ul className="space-y-2 text-sm text-white/30">
                  <li><Link to="/ressources" className="hover:text-white transition-colors">Hub Ressources</Link></li>
                  <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                  <li><Link to="/comparatif/ozcbnb-vs-smoobu" className="hover:text-white transition-colors">Alternative Smoobu</Link></li>
                  <li><Link to="/comparatif/ozcbnb-vs-lodgify" className="hover:text-white transition-colors">Alternative Lodgify</Link></li>
                  <li><Link to="/support" className="hover:text-white transition-colors">Support</Link></li>
                </ul>
              </div>
              <div>
                <p className="text-sm font-semibold text-white mb-4">Légal</p>
                <ul className="space-y-2 text-sm text-white/30">
                  <li><Link to="/mentions-légales" className="hover:text-white transition-colors">Mentions légales</Link></li>
                  <li><Link to="/confidentialite" className="hover:text-white transition-colors">Confidentialité</Link></li>
                  <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
                </ul>
              </div>
            </div>
            
            <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left">
              <p className="text-sm text-white/45">
                © 2025 OZCBNB • Créé avec passion par Ismail Özcan
              </p>
              <p className="text-sm text-white/45">
                SIREN 940 519 408 • ozcbnbcontact@gmail.com
              </p>
            </div>
          </div>
        </footer>

        <ChatBot />
      </div>
    </>
  );
}
